import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * The top of every page.
 *
 * Without slots for count/search/filters, each module lays its own header out
 * from scratch — so the first 80px of every module is slightly different and
 * moving between modules feels like moving between apps. The slots exist so a
 * page can express what it has without inventing a layout.
 *
 * Order is fixed on purpose: identity (icon, title, count) -> context
 * (subtitle, meta) -> input (search, filters) -> action. That order is what
 * makes two different modules feel like one product.
 *
 * Typography follows `elevated-polish-guide.md` > Typography Hierarchy:
 * a non-hero page title is `text-2xl font-black text-slate-900 tracking-tight`.
 */
export interface PageHeaderProps {
  title: string;
  /** Record count for a list page. Rendered as a pill next to the title. */
  count?: number | string;
  subtitle?: ReactNode;
  icon?: LucideIcon;
  /** Small facts under the title — last synced, owner, date range. */
  meta?: ReactNode;
  /** Search input. Sits on its own row so it can take the full width. */
  search?: ReactNode;
  /** Filter controls, rendered beside search. */
  filters?: ReactNode;
  /** One primary action, plus any secondary ones. */
  actions?: ReactNode;
  /** Pin to the top of a scrolling page. */
  sticky?: boolean;
  /**
   * Drop the bottom margin, for a header that sits in a fixed-height app shell
   * (the Support Inbox's list/detail split) rather than above scrolling content.
   *
   * This is a prop and not a `className` override on purpose: `mb-6` and any
   * `mb-*` a caller passes have equal specificity, so which one wins is decided
   * by Tailwind's stylesheet order (ascending scale), not by the class
   * attribute — `className="mb-0"` silently loses to `mb-6`.
   */
  dense?: boolean;
  className?: string;
}

export function PageHeader({
  title,
  count,
  subtitle,
  icon: Icon,
  meta,
  search,
  filters,
  actions,
  sticky = false,
  dense = false,
  className = '',
}: PageHeaderProps) {
  const hasControls = Boolean(search || filters);

  return (
    <div
      className={[
        dense ? 'mb-0' : 'mb-6',
        sticky
          ? 'sticky top-0 z-20 -mx-4 border-b border-slate-200/80 bg-slate-50/95 px-4 py-3 backdrop-blur'
          : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            {Icon && <Icon className="h-6 w-6 shrink-0 text-slate-700" />}
            <h1 className="truncate text-2xl font-black tracking-tight text-slate-900">{title}</h1>
            {count !== undefined && (
              <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold tabular-nums text-slate-600 ring-1 ring-slate-200">
                {count}
              </span>
            )}
          </div>
          {subtitle && (
            <p className={`mt-1 text-sm font-medium text-slate-500 ${Icon ? 'ml-9' : ''}`}>{subtitle}</p>
          )}
          {meta && (
            <div
              className={`mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-slate-500 ${
                Icon ? 'ml-9' : ''
              }`}
            >
              {meta}
            </div>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-3">{actions}</div>}
      </div>

      {hasControls && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          {search && <div className="min-w-0 flex-1">{search}</div>}
          {filters && <div className="flex shrink-0 flex-wrap items-center gap-2">{filters}</div>}
        </div>
      )}
    </div>
  );
}
